// components/PickerFieldTable.jsx
'use client';
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@mui/material';
import SelectListDialogTable from './SelectListDialogTable';

export default function PickerFieldTable({
  multiSelect = false,
  selectedItems = [],
  page,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  totalRecord,
  listOptions,
  value,
  options = [],
  columns = [],
  onChange,
  onSearch,
  onLoadMore, // NEW PROP
  isLoading = false,
  label = 'Select',
  placeholder = '',
  allowAdd = false,
  AddComponent = null,
  getOptionId = (o) => (o == null ? o : (o.id ?? o.value ?? o)),
  getOptionLabel = (o) => (o == null ? '' : (o.label ?? o.name ?? String(o))),
  getDisplayLabel,
  dialogTitle = null,
  disabled = false,
  helperText = '',
  dialogProps = {},
  addDialogProps = {},
  required = false,
  searchKey = 'name',
  searchPlaceholder,
  infoMessage = null,
  filterOptions = [],
  filterField = '',
  defaultFilterValue = 'all',
  isRowDisabled = null,
  error = false
}) {
  const [open, setOpen] = useState(false);

  const selectedLabel = useMemo(() => {
    if (multiSelect) {
      if (!Array.isArray(value) || value.length === 0) return '';
      const labels = value.map((val) => {
        const found =
          (options || []).find((o) => String(getOptionId(o)) === String(val)) ||
          (selectedItems || []).find((o) => String(getOptionId(o)) === String(val));
        if (found) return getDisplayLabel ? getDisplayLabel(found) : getOptionLabel(found);
        return String(val);
      });
      return labels.join(', ');
    } else {
      const found =
        (options || []).find((o) => String(getOptionId(o)) === String(value)) ||
        (selectedItems || []).find((o) => String(getOptionId(o)) === String(value));
      if (!found) return '';
      return getDisplayLabel ? getDisplayLabel(found) : getOptionLabel(found);
    }
  }, [options, value, getOptionId, getOptionLabel, getDisplayLabel, multiSelect, selectedItems]);

  return (
    <>
      <TextField
        fullWidth
        label={label}
        value={selectedLabel || ''}
        placeholder={placeholder}
        onClick={() => !disabled && setOpen(true)}
        error={error}
        helperText={helperText}
        disabled={disabled}
        slotProps={{
          input: {
            readOnly: true,
            sx: { cursor: disabled ? 'default' : 'pointer' }
          }
        }}
        required={required}
      />

      <SelectListDialogTable
        open={open}
        onClose={() => setOpen(false)}
        title={dialogTitle ?? label}
        options={options}
        listOptions={listOptions}
        columns={columns}
        value={value}
        onSearch={onSearch}
        onLoadMore={onLoadMore}
        searchKey={searchKey}
        searchPlaceholder={searchPlaceholder}
        isLoading={isLoading}
        onSelect={(v) => {
          onChange && onChange(v);
        }}
        allowAdd={allowAdd}
        AddComponent={AddComponent}
        onAddSuccess={(created) => {
          if (created == null) return;
          const id = (created && (created.id ?? created.value)) ?? created;
          if (multiSelect) {
            const currentArr = Array.isArray(value) ? value : [];
            onChange && onChange([...currentArr, id]);
          } else {
            onChange && onChange(id);
          }
        }}
        getOptionId={getOptionId}
        getOptionLabel={getOptionLabel}
        dialogProps={dialogProps}
        addDialogProps={addDialogProps}
        filterOptions={filterOptions}
        filterField={filterField}
        defaultFilterValue={defaultFilterValue}
        multiSelect={multiSelect}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        totalRecord={totalRecord}
        infoMessage={infoMessage}
        isRowDisabled={isRowDisabled}
      />
    </>
  );
}

PickerFieldTable.propTypes = {
  value: PropTypes.any,
  options: PropTypes.array,
  columns: PropTypes.array,
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  infoMessage: PropTypes.node,
  allowAdd: PropTypes.bool,
  AddComponent: PropTypes.elementType,
  getOptionId: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getDisplayLabel: PropTypes.func,
  dialogTitle: PropTypes.string,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  dialogProps: PropTypes.object,
  addDialogProps: PropTypes.object,
  required: PropTypes.bool
};
